/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useGetDivergenceQuery,
  useUpdateDivergenceBatchMutation
} from "@/services/divergence";
import { useSendEmailRequestEvidenceMutation } from "@/services/email";
import { useGetOneStoreConciliationQuery } from "@/services/store-conciliation";
import { transformObjectToArrayByKey } from "@/utils/formData";
import { DataTable } from "components/Datatable";
import { toast } from "components/ui/use-toast";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  IDivergence,
  IDivergenceWaitReturnsFormData,
} from "src/@types/divergence";
import { Paginated } from "src/@types/paginated";
import { MESSAGES } from "../../utils/messages";
import { useCurrentConciliation } from "../components/divergence-list/ducks/filter/hooks/statusConciliation";
import useColumns from "./columns";
import { validateText } from "./validate-form";
import WaitReturnFooter from "./wait-return-footer";

export default function WaitReturn() {
  const [updateDivergenceBatch, { isLoading: isUpdateLoading }] =
    useUpdateDivergenceBatchMutation();
  const [formErrors, setFormErrors] = useState<
    { [field: string]: string } | undefined
  >();
  const [sendEmailRequestEvidence, { isLoading: requestEmailLoading }] =
    useSendEmailRequestEvidenceMutation();
  const { id: conciliation_id, isClosed } = useCurrentConciliation();
  const { data: conciliation, isLoading: isConciliationLoading } = useGetOneStoreConciliationQuery({
    idTransaction: conciliation_id as string,
  });

  const {
    data: response,
    isLoading: isDivergencesLoading,
    isFetching,
  } = useGetDivergenceQuery({
    search: "",
    need_doc: true,
    conciliation_store_id: conciliation_id as string,
    include_email_status: "true",
    meta: {
      page: 1,
    },
  });
  const { data } = response ?? ({} as Paginated<IDivergence>);

  // Calcular itens disponíveis para envio (sem email em andamento)
  const availableItems = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.filter((item: IDivergence) => !item.has_email_solicitation);
  }, [data]);

  const hasAvailableItems = availableItems.length > 0;
  const isProcessing = isUpdateLoading || requestEmailLoading;
  const isDataLoading = isDivergencesLoading || isFetching || isConciliationLoading;

  const methods = useForm();
  const { handleSubmit: handleFormSubmit } = methods;
  const columns = useColumns(methods, formErrors);
  const { handleModal } = useDialogModal();

  // Razões de desabilitação que NÃO dependem dos comentários
  const baseDisabledReason = useMemo((): string | null => {
    if (isClosed) return "Conciliação fechada";
    if (isDataLoading) return "Carregando dados...";
    if (!data || data.length === 0) return "Nenhuma divergência encontrada";
    if (!hasAvailableItems) return "Todas as divergências já possuem email em andamento";
    if (isProcessing) return "Processando...";
    return null;
  }, [isClosed, isDataLoading, data, hasAvailableItems, isProcessing]);

  const handleSave = async (data: IDivergenceWaitReturnsFormData) => {
    const divergenceArray = transformObjectToArrayByKey(data, "divergence");
    const store_code = conciliation?.store?.code;

    if (!conciliation_id || !store_code) return;

    // Filtrar apenas divergências que têm id e version (as que não têm email em andamento)
    const validDivergenceArray = divergenceArray.filter((d: any) =>
      d.value.id !== undefined && d.value.version !== undefined
    );

    const validated =
      validateText(validDivergenceArray, setFormErrors) &&
      validDivergenceArray.filter((data) =>
        Object.values(data.value).every((item) => item !== undefined)
      ).length !== 0;
    if (!validated || isUpdateLoading) {
      return;
    }

    const formattedDivergenceArray = validDivergenceArray.map((d: any) => {
      const analystValue = typeof d.value.analyst === 'string' ? d.value.analyst.trim() : '';
      const item: any = {
        id: d.value.id,
        version: d.value.version,
      };

      // Só incluir analyst_comments se tiver valor não-vazio
      if (analystValue.length > 0) {
        item.analyst_comments = analystValue;
      }

      // Só incluir has_dependency se explicitamente definido
      if (typeof d.value.has_dependency === 'boolean') {
        item.has_dependency = d.value.has_dependency;
      }

      // Só incluir dependency_date se has_dependency e date válidos
      if (d.value.has_dependency && d.value.dependency_date) {
        item.dependency_date = d.value.dependency_date;
      }

      return item;
    });

    if (formattedDivergenceArray.length === 0 || isUpdateLoading) {
      return;
    }

    // 1. Salvar dados ANTES de enviar email
    try {
      await updateDivergenceBatch({
        conciliation_id: Number(conciliation_id),
        divergences: formattedDivergenceArray
      }).unwrap();
    } catch (e) {
      toast(MESSAGES.UPDATE.ERROR);
      return; // Se falhar ao salvar, NÃO enviar email
    }

    // 2. Só envia email após dados salvos com sucesso
    try {
      await sendEmailRequestEvidence({
        conciliation_id,
        store_code,
      }).unwrap();

      handleModal({ isOpen: false });

      toast({
        variant: "default",
        description: "Dados salvos e evidência solicitada com sucesso!",
        duration: 5000,
      });
    } catch (error: any) {
      const isStoreNotAllowed = error?.status === 400 &&
        error?.data?.messages?.includes("Store not allowed to receive emails");

      toast({
        variant: "destructive",
        description: isStoreNotAllowed
          ? "Esta loja não tem permissão para receber emails. Os dados foram salvos."
          : "Erro ao solicitar evidência. Os dados foram salvos.",
        duration: 5000,
      });
    }
  };

  return (
    <>
      <div className="">
        <DataTable
          data={data}
          columns={columns}
          emptyMessage="Selecione uma data de conciliação para ver os dados."
          isLoading={isFetching}
        />
        <WaitReturnFooter
          methods={methods}
          availableItems={availableItems}
          baseDisabledReason={baseDisabledReason}
          isProcessing={isProcessing}
          onClose={() => handleModal({ isOpen: false })}
          onSubmit={async () => await handleFormSubmit(handleSave)()}
        />
      </div>
    </>
  );
}
